import { CookedProduct, CookedProductCatalogItem, isPricedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { CustomItemDataType, ProductCatalogCollectionMembersFragment } from '@graphql/generated';
import { CashFundProduct } from '../../AddCashFund';
import { AddRegistryItemClickedProps, AddRegistryMethodType, CatalogPageTelemetry } from '../';
import { CurationState } from '../state/Catalog';
import { CatalogDepartmentDictionaryEntry } from '../state/Catalog';
import { deriveProductCatalogCategoryType } from './deriveTelemetryPage';
import { useCallback, useMemo } from 'react';
import { useSearchTermQueryParam } from './queryParams';
import { CatalogItemTile } from '../Catalog.types';
import { useAuth } from '@shared/components/AuthProvider';
import { useShopStorefrontContext } from '../components/ShopStorefrontProvider';
import { universalItemSourceToRegistryItemSource } from './registryItemMetadata';
import { RegistryItemSource } from '../Catalog.controller';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';

export type MinimalProductWithDetails =
  | Pick<CatalogItemTile, 'id' | 'title' | 'itemType' | 'source' | 'price' | 'brand' | 'productUrl' | 'rating'>
  | (NonNullable<ProductCatalogCollectionMembersFragment['catalogItemData']> & Pick<CatalogItemTile, 'source'>);

type BaseAddRegistryItemArgs = {
  eventId: Maybe<string>;
  method: AddRegistryMethodType;
  page: CatalogPageTelemetry;
  defaultPage?: CatalogPageTelemetry;
  itemPosition: Maybe<number>;
  qty: number;
  storedCuration: Maybe<CurationState>;
  selectedCategory: Maybe<CatalogDepartmentDictionaryEntry>;
  itemType?: CustomItemDataType;
  categoryOverride?: string;
  productPromoName?: string;
  productPromoPosition?: number;
  product: Maybe<{ id: string }>;
  origin?: string;
  isOnboarding?: boolean;
  metadataSource?: Maybe<RegistryItemSource>;
};

export type UseCreateAddRegistryItemClickPayloadReturn = ReturnType<typeof useCreateAddRegistryItemClickPayload>;
export const useCreateAddRegistryItemClickPayload = () => {
  const { currentStorefront } = useShopStorefrontContext();
  const searchTerm = useSearchTermQueryParam();
  const { currentUser } = useAuth();
  const email = useMemo(() => currentUser.profile?.email, [currentUser]);

  const createMinimimAddRegistryItemPayload = useCallback(
    (args: BaseAddRegistryItemArgs): AddRegistryItemClickedProps => {
      const {
        eventId,
        itemType = CustomItemDataType.gift,
        itemPosition,
        method,
        categoryOverride,
        page,
        product,
        productPromoName,
        productPromoPosition,
        qty,
        storedCuration,
        selectedCategory,
        origin,
        isOnboarding,
        metadataSource,
      } = args;

      const productCatalogCategory = storedCuration || selectedCategory;

      const payload: AddRegistryItemClickedProps & { categoryOverride?: string } = {
        eventId,
        orderId: Date.now(),
        email,
        method: method,
        categoryOverride,
        page,
        productCatalogItemPosition: itemPosition || undefined,
        totalRequested: qty,
        itemType,
        productCatalogCategoryId: productCatalogCategory?.id,
        productCatalogCategoryTitle: productCatalogCategory?.title,
        productCatalogCategoryPublicUrlPath: productCatalogCategory?.publicUrlPath,
        productCatalogCategoryType: deriveProductCatalogCategoryType({ product, selectedCategory, curation: storedCuration }), //storedCuration && !selectedCategory ? 'curation' :
        promoName: productPromoName,
        productPromoPosition: productPromoPosition,
        origin,
        isOnboarding,
        searchTerm,
        shopStorefront: currentStorefront,
        metadataSource,
      };

      if (isOnboarding) {
        payload.productCatalogCategoryId = undefined;
        payload.productCatalogCategoryPublicUrlPath = undefined;
        payload.productCatalogCategoryTitle = undefined;
        payload.productCatalogCategoryType = undefined;
      }

      return payload;
    },
    [searchTerm, email, currentStorefront],
  );

  const deriveStandardProductPayload = useCallback(
    (
      args: BaseAddRegistryItemArgs & {
        product: Maybe<MinimalProductWithDetails>;
      },
    ): AddRegistryItemClickedProps & { categoryOverride?: string } => {
      const { product } = args;

      const payload = createMinimimAddRegistryItemPayload(args);

      if (product) {
        payload.productSource = universalItemSourceToRegistryItemSource(product.source);
        payload.productCatalogItemId = product.id;
        payload.productUrl = product.productUrl;
        payload.productCatalogItemTitle = product.title;
        payload.priceValueInMinorUnits = product.price?.valueInMinorUnits;
        payload.priceCurrencyCode = product.price?.currency?.code;
        payload.productCatalogBrandId = product.brand?.id;
        payload.productCatalogBrandTitle = product.brand?.name;
        payload.rating = product.rating?.stats.averageRating || -1;
      }

      return payload;
    },
    [createMinimimAddRegistryItemPayload],
  );

  const deriveCashFundPayload = useCallback(
    (
      args: Merge<
        BaseAddRegistryItemArgs,
        | {
            product: Maybe<CookedProduct | CookedProductCatalogItem>;
            customItemFormFields: null;
            itemType: CustomItemDataType.cash | CustomItemDataType.charity | undefined;
          }
        | {
            product: null;
            customItemFormFields: CashFundProduct;
            itemType: CustomItemDataType.cash | CustomItemDataType.charity | undefined;
          }
      >,
    ): AddRegistryItemClickedProps => {
      const { product, customItemFormFields } = args;
      const payload = createMinimimAddRegistryItemPayload(args);

      if (customItemFormFields) {
        const { id, name, amount, qty, currencyCode } = customItemFormFields;
        payload.productCatalogItemId = id || undefined;
        payload.productCatalogItemTitle = name;
        payload.priceValueInMinorUnits = amount ? amount * 100 : 0;
        payload.totalRequested = qty;
        payload.priceCurrencyCode = currencyCode;
      } else if (product) {
        const { donationFund } = product;
        payload.productUrl = donationFund?.fund.url;
        payload.productCatalogItemId = product.id;
        payload.productCatalogItemTitle = product.title;
        payload.totalRequested = product.requested;
        payload.priceValueInMinorUnits = donationFund?.fund.goalMonetaryValue?.valueInMinorUnits;
        payload.priceCurrencyCode = (isPricedProduct(product) && product.currencyCode) || DEFAULT_CURRENCY;
      }

      return payload;
    },
    [createMinimimAddRegistryItemPayload],
  );

  return {
    deriveCashFundPayload,
    deriveStandardProductPayload,
  };
};
